/**
 * MapLibre GL JS map for the public "Reported Concerns" page (public-concerns-map.md / ADR-0023,
 * Sue's mockup) — self-hosted PMTiles basemap (@protomaps/basemaps style layers), clustered pins
 * with zoom-to-expand, status-colored + status-SHAPED markers (never color-alone, WCAG 1.4.1), a
 * legend, and a selected-pin popover showing EXACTLY the list's fields (category/status/dates —
 * never more, per the spec). Co-equal with the accessible `<ConcernsTable>` — selecting a pin
 * raises `onSelect(ref)`; the page keeps map+table selection in sync in both directions via the
 * `selectedRef` prop (a pin flies-to + its popover opens when the TABLE row is activated).
 *
 * No CSP change needed — tiles, glyph fonts, AND the sprite are same-origin: tiles via
 * `pmtiles://${origin}/maps/{tenantId}.pmtiles`, fonts via self-hosted PBFs at
 * `${origin}/maps/fonts/{fontstack}/{range}.pbf` (the CSP's `connect-src 'self'` blocked the
 * original protomaps.github.io glyph host, blanking the map on every real browser — see
 * `remapToAvailableFonts` below for the one fontstack we don't self-host), and the sprite
 * (road-shield/POI icons) via self-hosted sprite sheets at `${origin}/maps/sprites/{flavor}`
 * (MapLibre auto-appends `.json`/`.png`/`@2x` — see `buildStyle` below). `worker-src 'self' blob:`
 * is already allowed (infra/modules/hosting/main.tf).
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardedRef,
} from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { layers as protomapsLayers, namedFlavor } from "@protomaps/basemaps";
import type { PublicConcernStatus } from "../api/types.js";
import { PUBLIC_CONCERN_STATUS_LABEL } from "./ConcernStatusPill.js";
import { shortDate } from "./format.js";

// One Protocol instance per page load (maplibregl.addProtocol is a GLOBAL registry — registering
// twice across remounts is harmless/idempotent, but we only ever need the one instance).
let pmtilesProtocolRegistered = false;
function ensurePmtilesProtocol(): void {
  if (pmtilesProtocolRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);
  pmtilesProtocolRegistered = true;
}

const SOURCE_ID = "td-basemap";
const PINS_SOURCE_ID = "td-concern-pins";
const CLUSTER_RADIUS = 50;
/** How long to wait for the MapLibre `load` event before treating the style as failed. */
const STYLE_LOAD_TIMEOUT_MS = 10_000;

/** Fontstacks we actually self-host at `/maps/fonts/{fontstack}/{range}.pbf` (shipped manually via
 *  scripts/deploy-pmtiles.sh — see memory `public-concerns-map`). Protomaps' own asset host doesn't
 *  ship "Noto Sans Bold" either (it 404s there too), so `@protomaps/basemaps`'s `protomapsLayers`
 *  output — which references Bold for e.g. cluster-count labels — must be remapped to an available
 *  weight rather than left to 404/CSP-block. */
export const AVAILABLE_FONTSTACKS = new Set([
  "Noto Sans Regular",
  "Noto Sans Medium",
  "Noto Sans Italic",
]);
const FONT_FALLBACK = "Noto Sans Medium";

/** Remaps a literal font-stack array (e.g. `["Noto Sans Medium"]`) to only reference self-hosted
 *  fontstacks — anything not in `AVAILABLE_FONTSTACKS` (i.e. "Noto Sans Bold") becomes
 *  `FONT_FALLBACK`. */
function remapFontStack(fonts: string[]): string[] {
  return fonts.map((font) => (AVAILABLE_FONTSTACKS.has(font) ? font : FONT_FALLBACK));
}

/** Remaps a `text-font` value to only reference self-hosted fontstacks. Protomaps emits either a
 *  plain literal font-stack array (the common case, e.g. `["Noto Sans Medium"]`) OR a conditional
 *  expression WRAPPING literal font-stacks in an explicit `["literal", [...]]` (e.g.
 *  `places_locality`'s zoom-dependent weight: `["case", cond, ["literal",["Noto Sans Medium"]], ...]`).
 *  Only remap strings found (a) at the TOP level, when `text-font` itself is a plain font-stack array
 *  with no expression wrapper, or (b) inside an explicit `["literal", [...]]` wrapper — never while
 *  recursing through other expression nodes, since operators like `["get", "min_zoom"]` or
 *  `["<=", x, 5]` are ALSO arrays-of-strings-or-literals at that depth and must be left untouched.
 *  Mistakenly remapping those corrupts the expression (see `public-concerns-map` memory — this exact
 *  bug blanked the production basemap). `topLevel` is only ever true for the initial call. */
function remapTextFont(value: unknown, topLevel = false): unknown {
  if (!Array.isArray(value)) return value;
  if (topLevel && value.every((v) => typeof v === "string")) {
    // The plain-font-stack case: text-font: ["Noto Sans Medium"] with no expression at all.
    return remapFontStack(value as string[]);
  }
  if (value[0] === "literal" && Array.isArray(value[1])) {
    // ["literal", ["Noto Sans Medium"]] — remap the wrapped font-stack, keep the wrapper.
    return ["literal", remapFontStack(value[1] as string[])];
  }
  // Any other expression node (case/match/get/comparisons/...) — recurse, don't remap strings here.
  return value.map((v) => remapTextFont(v));
}

/** Rewrites every layer's `layout["text-font"]` entry (literal stack or expression) to only
 *  reference self-hosted fontstacks. See `remapTextFont` for why this must recurse rather than
 *  flat-map. */
export function remapToAvailableFonts(
  layers: maplibregl.LayerSpecification[],
): maplibregl.LayerSpecification[] {
  return layers.map((layer) => {
    const layout = (layer as { layout?: Record<string, unknown> }).layout;
    const textFont = layout?.["text-font"];
    if (textFont === undefined) return layer;
    return {
      ...layer,
      layout: { ...layout, "text-font": remapTextFont(textFont, true) },
    } as maplibregl.LayerSpecification;
  });
}

/** Builds the MapLibre style spec for a given tenant/theme — same-origin sources only (pmtiles
 *  vector tiles, self-hosted glyph PBFs, self-hosted sprite sheets), so no CSP change is ever
 *  needed. Pulled out as its own pure function (rather than inlined in the init effect) so the
 *  sprite/glyphs/tile-URL wiring is unit-testable without standing up a real MapLibre/WebGL
 *  context (jsdom has none — see the init effect's try/catch below). `sprite` has NO extension;
 *  MapLibre itself appends `.json` for the index and `.png`/`@2x.png` for the image depending on
 *  `devicePixelRatio` — see maplibre-gl's style spec for `sprite`. */
export function buildStyle(
  origin: string,
  tenantId: string,
  dark: boolean,
): maplibregl.StyleSpecification {
  const flavor = namedFlavor(dark ? "dark" : "light");
  const tileUrl = `pmtiles://${origin}/maps/${encodeURIComponent(tenantId)}.pmtiles`;
  return {
    version: 8,
    glyphs: `${origin}/maps/fonts/{fontstack}/{range}.pbf`,
    sprite: `${origin}/maps/sprites/${dark ? "dark" : "light"}`,
    sources: {
      [SOURCE_ID]: { type: "vector", url: tileUrl, attribution: "© OpenStreetMap contributors" },
    },
    layers: remapToAvailableFonts(protomapsLayers(SOURCE_ID, flavor, { lang: "en" })),
  };
}

/** Status -> marker FILL color (CSS custom property, resolved against the page's own computed
 *  style so the map stays on-theme without re-implementing the token ramp in JS). Status -> marker
 *  SHAPE is a maplibre `symbol`/`circle` layer filter below — color is never the only signal. */
const STATUS_TOKEN: Record<PublicConcernStatus, string> = {
  open: "acknowledged",
  "in-progress": "inprogress",
  resolved: "resolved",
};

function readCssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Builds the FeatureCollection maplibre clusters — one Point Feature per pin, carrying `status`/
 *  `ref`/`category`/dates as properties so the click handler + popover never need a second lookup.
 *  `statusToken` is pre-resolved here (not left as the raw `status` value) so the paint layer below
 *  can stay a single DATA-DRIVEN `match` expression over `--td-status-{token}-dot`, regardless of
 *  how many distinct statuses the caller's status union has (3 for the public page, 7 for staff). */
function toFeatureCollection<S extends string>(
  pins: ConcernsMapPin<S>[],
  statusConfig: ConcernsMapStatusConfig<S>,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: "FeatureCollection",
    features: pins.map((p) => ({
      type: "Feature",
      id: p.ref,
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: {
        ref: p.ref,
        category: p.category,
        type: p.type,
        status: p.status,
        statusToken: statusConfig.token(p.status),
        location: p.location,
        dateReported: p.dateReported,
        dateResolved: p.dateResolved,
        staffOnly: p.staffOnly === true,
      },
    })),
  };
}

/** Status -> token/label resolver pair, injected so this map can be reused by a caller with a
 *  DIFFERENT status vocabulary than the public 3-way scope (the staff concerns map, which needs
 *  the full internal RequestStatus lifecycle — see StaffConcernsMapPage). Defaults to the public
 *  PublicConcernStatus mapping below when omitted, so ConcernsMapPage (the only other caller as of
 *  this writing) needs no prop changes at all. */
export interface ConcernsMapStatusConfig<S extends string> {
  token: (status: S) => string;
  label: (status: S) => string;
}

const PUBLIC_STATUS_CONFIG: ConcernsMapStatusConfig<PublicConcernStatus> = {
  token: (s) => STATUS_TOKEN[s],
  label: (s) => PUBLIC_CONCERN_STATUS_LABEL[s],
};

/** Minimal pin shape this component needs — PublicConcernPin already satisfies it; the staff
 *  concerns map's StaffConcernPin (api/types.ts) does too, as long as its unique identifier is
 *  passed in as `ref` by the caller (ConcernsMapPins below, or the caller maps staff pins to this
 *  shape before passing them in — see StaffConcernsMapPage). */
export interface ConcernsMapPin<S extends string> {
  ref: string;
  category: string;
  type?: string;
  status: S;
  lat: number;
  lng: number;
  location?: string;
  dateReported: string;
  dateResolved: string | null;
  /** Staff-only/internal item (never present on the public feed) — rendered with a distinguishing
   *  ring so staff can tell internal engineering items from resident-facing reports at a glance. */
  staffOnly?: boolean;
}

export interface ConcernsMapProps<S extends string = PublicConcernStatus> {
  tenantId: string;
  pins: ConcernsMapPin<S>[];
  /** Selected pin's `ref` (synced bidirectionally with the table) — null = nothing selected. */
  selectedRef: string | null;
  onSelect: (ref: string | null) => void;
  dark: boolean;
  /** Status vocabulary — defaults to the public 3-way (open/in-progress/resolved) scope. Pass a
   *  config keyed to a different status union (e.g. @td/shared's full RequestStatus) to reuse this
   *  component for a different status taxonomy entirely (the staff map). */
  statusConfig?: ConcernsMapStatusConfig<S>;
  /** Legend items — defaults to the public 3-way open/in-progress/resolved legend. Pass a full set
   *  when `statusConfig` covers a different vocabulary (e.g. the staff map's 7-status lifecycle),
   *  or the legend would otherwise keep showing the public page's 3 entries regardless. */
  legendItems?: ConcernsMapLegendItem[];
  /** Extra HTML appended to the end of the selected-pin popover's content (e.g. a "View details"
   *  link to the staff report detail page) — escaped/sanitized by the CALLER, not this component.
   *  Pair with `onOpenDetail` below for the actual navigation (a raw `<a href>` inside MapLibre's
   *  popup HTML would do a full page reload, not an SPA nav) — render a
   *  `<button class="td-concern-popover__detail-link" data-ref="{ref}">` (or similar) and this
   *  component wires its click to `onOpenDetail(ref)` via one delegated listener. */
  popoverExtraHtml?: (pin: ConcernsMapPin<S>) => string;
  /** Called when the caller's "View details" button (popoverExtraHtml above) is clicked, with the
   *  clicked pin's `ref`. Omit `popoverExtraHtml`/`onOpenDetail` entirely for the public page's
   *  original behavior (no detail link at all). */
  onOpenDetail?: (ref: string) => void;
}

/** Imperative handle so a parent (ConcernsMapPage's tab view) can force a MapLibre `resize()` call
 *  after this component becomes visible again — see `resize()`'s own doc comment below for why
 *  that's required at all. */
export interface ConcernsMapHandle {
  /** Resizes the underlying MapLibre canvas to its container's current size. REQUIRED after this
   *  component goes from `display: none` -> visible (e.g. a tabbed "Map" panel becoming active):
   *  MapLibre measures its container ONCE at construction time, so a map built while its panel was
   *  hidden initializes with a 0×0 canvas and stays blank/grey forever unless told to re-measure. A
   *  no-op (safe to call any time, including before the map has finished loading) when there's no
   *  live map instance yet. */
  resize: () => void;
}

// forwardRef doesn't infer a type param from its own generic signature — the inner function is
// typed generically and the exported const is cast back to a generic callable (a well-known
// forwardRef+generics pattern), so every caller (ConcernsMapPage AND StaffConcernsMapPage) still
// gets full type inference on its own status union.
function ConcernsMapInner<S extends string = PublicConcernStatus>(
  {
    tenantId,
    pins,
    selectedRef,
    onSelect,
    dark,
    statusConfig,
    legendItems,
    popoverExtraHtml,
    onOpenDetail,
  }: ConcernsMapProps<S>,
  handleRef: ForwardedRef<ConcernsMapHandle>,
): JSX.Element {
  const resolvedStatusConfig = (statusConfig ??
    (PUBLIC_STATUS_CONFIG as unknown as ConcernsMapStatusConfig<S>)) satisfies ConcernsMapStatusConfig<S>;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useImperativeHandle(
    handleRef,
    () => ({
      resize: () => {
        mapRef.current?.resize();
      },
    }),
    [],
  );

  // Init once per mount.
  useEffect(() => {
    if (!containerRef.current) return;
    ensurePmtilesProtocol();
    const style = buildStyle(window.location.origin, tenantId, dark);

    // Defense-in-depth: a WebGL-less environment (headless test runners, a browser with GL
    // disabled, an unsupported old device) must degrade to the error message below, never crash
    // the whole page — the accessible <ConcernsTable> still renders every reported concern.
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style,
        center: [-94.75, 38.17], // placeholder; refit to the tenant's own pins once loaded below
        zoom: 12,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[ConcernsMap] map could not initialize (likely WebGL unavailable):", err);
      setLoadError(true);
      return;
    }
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    // A single missing glyph range or a transient tile 404 must NOT blank the whole map — only a
    // genuine init/style failure should (handled by the try/catch above + the load-timeout below).
    // Resource-level errors are still surfaced to the console for debuggability.
    map.on("error", (e) => {
      // eslint-disable-next-line no-console
      console.warn("[ConcernsMap] MapLibre resource error (non-fatal):", e.error ?? e);
    });
    const loadTimeout = setTimeout(() => setLoadError(true), STYLE_LOAD_TIMEOUT_MS);
    map.on("load", () => {
      clearTimeout(loadTimeout);
      setReady(true);
    });

    return () => {
      clearTimeout(loadTimeout);
      map.remove();
      mapRef.current = null;
    };
    // Intentionally only re-inits on tenant/theme change — pins are applied via the data-driven
    // effect below so a filter change never tears down/rebuilds the whole GL context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, dark]);

  // Pins + clustering layers — added once the style has loaded, re-applied whenever `pins` changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const data = toFeatureCollection(pins, resolvedStatusConfig);
    const existing = map.getSource(PINS_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
    if (existing) {
      existing.setData(data);
    } else {
      map.addSource(PINS_SOURCE_ID, {
        type: "geojson",
        data,
        cluster: true,
        clusterRadius: CLUSTER_RADIUS,
        clusterMaxZoom: 16,
      });

      const ink = readCssVar("--td-ink-invert", "#fbfaf7");
      // A ring-thickness-per-status fallback ramp for an unrecognized token (defense-in-depth —
      // should never actually be hit since `statusToken` above is always one of the caller's own
      // status values) — never used for color (color is purely the `match` expression below).
      const DEFAULT_RING = 2;

      // Cluster circles — size/shade scale with count; label shows the count (text is still the
      // primary signal, never relying on circle size alone).
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: PINS_SOURCE_ID,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": readCssVar("--td-accent", "#2f6b4f"),
          "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26],
          "circle-stroke-width": 2,
          "circle-stroke-color": ink,
        },
      });
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: PINS_SOURCE_ID,
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": [FONT_FALLBACK],
          "text-size": 13,
        },
        paint: { "text-color": ink },
      });

      // Unclustered pins — ONE data-driven layer (not one filtered layer per status): `circle-color`
      // reads --td-status-{statusToken}-dot per feature via a `match` expression, so this scales to
      // any number of distinct statuses (3 for the public page's open/in-progress/resolved scope, 7
      // for the staff map's full RequestStatus lifecycle) without adding a layer per status. Color
      // is never the only signal — the accompanying legend/table glyphs carry shape/text too.
      map.addLayer({
        id: "pins",
        type: "circle",
        source: PINS_SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match",
            ["get", "statusToken"],
            "acknowledged",
            readCssVar("--td-status-acknowledged-dot", "#b9791f"),
            "inprogress",
            readCssVar("--td-status-inprogress-dot", "#7e52c0"),
            "resolved",
            readCssVar("--td-status-resolved-dot", "#2f8b58"),
            "new",
            readCssVar("--td-status-new-dot", "#2e6fb0"),
            "closed",
            readCssVar("--td-status-closed-dot", "#78786c"),
            "duplicate",
            readCssVar("--td-status-duplicate-dot", "#78786c"),
            "rejected",
            readCssVar("--td-status-rejected-dot", "#b84343"),
            readCssVar("--td-status-acknowledged-dot", "#b9791f"), // fallback
          ],
          "circle-radius": 8,
          "circle-stroke-width": DEFAULT_RING,
          "circle-stroke-color": ink,
        },
      });

      // StaffOnly/internal-item ring (staff-concerns-map.md) — a thicker, high-contrast outer ring
      // drawn ON TOP of the pin layer for any feature flagged `staffOnly`, so internal engineering
      // items are visually distinguishable from resident-facing reports at a glance, on top of
      // (not instead of) the status color. Public callers never pass staffOnly:true pins, so this
      // layer is simply a no-op filter match there.
      map.addLayer({
        id: "pin-staffonly-ring",
        type: "circle",
        source: PINS_SOURCE_ID,
        filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "staffOnly"], true]],
        paint: {
          "circle-color": "rgba(0,0,0,0)",
          "circle-radius": 12,
          "circle-stroke-width": 2,
          "circle-stroke-color": readCssVar("--td-accent", "#2f6b4f"),
          "circle-stroke-opacity": 0.9,
        },
      });

      // Zoom-to-expand on a cluster click (required behavior).
      map.on("click", "clusters", (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
        const clusterId = features[0]?.properties?.cluster_id;
        const source = map.getSource(PINS_SOURCE_ID) as maplibregl.GeoJSONSource;
        if (clusterId === undefined) return;
        source.getClusterExpansionZoom(clusterId).then((zoom) => {
          const coords = (features[0]!.geometry as GeoJSON.Point).coordinates as [number, number];
          map.easeTo({ center: coords, zoom });
        });
      });
      for (const layerId of ["pins", "pin-staffonly-ring"]) {
        map.on("mouseenter", layerId, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
        });
        map.on("click", layerId, (e) => {
          const ref = e.features?.[0]?.properties?.ref as string | undefined;
          if (ref) onSelect(ref);
        });
      }
      map.on("click", (e) => {
        const hit = map.queryRenderedFeatures(e.point, {
          layers: ["clusters", "pins", "pin-staffonly-ring"],
        });
        if (hit.length === 0) onSelect(null);
      });
    }

    // Fit the viewport to the pins on first load (never implies precision beyond the fuzz radius —
    // this is a VIEWPORT fit, not a claim about exact location).
    if (pins.length > 0 && !existing) {
      const bounds = new maplibregl.LngLatBounds();
      for (const p of pins) bounds.extend([p.lng, p.lat]);
      map.fitBounds(bounds, { padding: 48, maxZoom: 15, animate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, pins]);

  // Selected-pin popover (co-equal with the table: selecting a TABLE row also opens this).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    popupRef.current?.remove();
    popupRef.current = null;
    if (!selectedRef) return;
    const pin = pins.find((p) => p.ref === selectedRef);
    if (!pin) return;
    map.easeTo({ center: [pin.lng, pin.lat], zoom: Math.max(map.getZoom(), 14) });
    const statusLabel = resolvedStatusConfig.label(pin.status);
    const statusToken = resolvedStatusConfig.token(pin.status);
    const resolvedLine = pin.dateResolved
      ? `<div class="td-concern-popover__row td-mono">Resolved ${shortDate(pin.dateResolved)}</div>`
      : "";
    // Same PII-safe street-level text as the table's "Location (approx.)" column (ADR-0023
    // two-tier location) — omitted entirely when empty, never rendered as a blank line.
    const locationLine = pin.location
      ? `<p class="td-concern-popover__location">near ${escapeHtml(pin.location)}</p>`
      : "";
    // Type replaces category as the bold headline (concerns-map-redesign-spec.md) — omitted
    // gracefully when empty, never rendered as a blank headline.
    const typeLine = pin.type
      ? `<span class="td-concern-popover__type">${escapeHtml(pin.type)}</span>`
      : "";
    // Internal-view caption (staff-concerns-map.md) — only ever present when the pin itself is
    // flagged staffOnly AND the caller opted into it via popoverExtraHtml below rendering it; this
    // component itself stays public-page-agnostic about the badge TEXT, just the flag's existence.
    const staffOnlyBadge = pin.staffOnly
      ? `<span class="td-concern-popover__staffonly">Internal</span>`
      : "";
    const extraHtml = popoverExtraHtml ? popoverExtraHtml(pin) : "";
    // Fixed 216px width (maxWidth kept in lockstep so MapLibre doesn't auto-grow the popup back to
    // full content width) — sized to fit, never overwhelming the map (concerns-map-redesign-spec.md).
    const popup = new maplibregl.Popup({ closeOnClick: false, offset: 12, maxWidth: "216px" })
      .setLngLat([pin.lng, pin.lat])
      // Content order: Type (bold) -> status pill -> "near <street>" -> dates -> caller's extra
      // content (e.g. "View details") -> explicit close button. Public callers never set staffOnly
      // or popoverExtraHtml, so this is byte-identical to the pre-staff-map markup for them.
      .setHTML(
        `<div class="td-concern-popover">` +
          `<button class="td-concern-popover__close" aria-label="Close">×</button>` +
          typeLine +
          staffOnlyBadge +
          // Same `.td-pill`/`.td-pill__dot` shell + color tokens as <ConcernStatusPill> (color is
          // inline-styled there too, not a per-status modifier class — this raw-HTML popup mirrors
          // that convention rather than inventing a new `.td-pill--{status}` class scheme).
          `<div class="td-concern-popover__row">` +
          `<span class="td-pill td-pill--sm" style="color:var(--td-status-${statusToken}-fg);background:var(--td-status-${statusToken}-bg)">` +
          `<span class="td-pill__dot" style="background:var(--td-status-${statusToken}-dot)"></span>${escapeHtml(statusLabel)}</span>` +
          `</div>` +
          locationLine +
          `<div class="td-concern-popover__row td-mono">Reported ${shortDate(pin.dateReported)}</div>` +
          resolvedLine +
          extraHtml +
          `</div>`,
      )
      .addTo(map);
    popup.on("close", () => onSelect(null));
    popup
      .getElement()
      .querySelector(".td-concern-popover__close")
      ?.addEventListener("click", () => popup.remove());
    // Delegated click for the caller's "View details" button (popoverExtraHtml) — a plain `<button
    // data-ref="...">`, never a raw `<a href>` (which would full-page-reload instead of an SPA nav).
    if (onOpenDetail) {
      popup
        .getElement()
        .querySelector(".td-concern-popover__detail-link")
        ?.addEventListener("click", () => onOpenDetail(pin.ref));
    }
    popupRef.current = popup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRef, ready]);

  return (
    <div className="td-concerns-map">
      <div
        ref={containerRef}
        className="td-concerns-map__canvas"
        role="application"
        aria-label="Map of reported concerns"
      />
      {loadError ? (
        <p className="td-concerns-map__error" role="status">
          The map couldn't be displayed — the list below shows every reported concern.
        </p>
      ) : null}
      <ConcernsMapLegend
        items={
          legendItems ??
          PUBLIC_LEGEND_ITEMS.map(({ status, token }) => ({
            key: status,
            token,
            label: PUBLIC_CONCERN_STATUS_LABEL[status],
          }))
        }
      />
    </div>
  );
}

// forwardRef + a generic inner function: forwardRef's own type signature can't express a type
// param, so it infers ConcernsMapInner's props as a single non-generic shape — the exported
// binding is cast back to the real generic callable (a well-known forwardRef+generics pattern) so
// every caller keeps full inference on its own status union — see ConcernsMapInner's own comment.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- forwardRef can't express a
// generic render function's type param; the cast below restores it for every caller.
export const ConcernsMap = forwardRef(ConcernsMapInner as any) as <
  S extends string = PublicConcernStatus,
>(
  props: ConcernsMapProps<S> & { ref?: ForwardedRef<ConcernsMapHandle> },
) => JSX.Element;

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}

const PUBLIC_LEGEND_ITEMS: { status: PublicConcernStatus; token: string }[] = [
  { status: "open", token: STATUS_TOKEN.open },
  { status: "in-progress", token: STATUS_TOKEN["in-progress"] },
  { status: "resolved", token: STATUS_TOKEN.resolved },
];

/** Legend item shape generalized so a caller with a different status vocabulary (the staff map's
 *  full RequestStatus lifecycle) can supply its own set — the public page's default call site above
 *  is unchanged (still the 3-way open/in-progress/resolved legend). */
export interface ConcernsMapLegendItem {
  key: string;
  token: string;
  label: string;
}

export function ConcernsMapLegend({ items }: { items: ConcernsMapLegendItem[] }): JSX.Element {
  return (
    <ul className="td-concerns-map__legend" aria-label="Map legend">
      {items.map(({ key, token, label }) => (
        <li key={key}>
          <span
            className="td-concerns-map__legend-dot"
            style={{ background: `var(--td-status-${token}-dot)` }}
            aria-hidden="true"
          />
          {label}
        </li>
      ))}
    </ul>
  );
}
